import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import React from 'react';
import {
  formatApiErrorMessage,
  getApiErrorRawMessage,
  isApiErrorBody,
  markRequestErrorHandled,
  type ApiErrorBody,
} from '@/utils/apiError';
import {
  clearAuthToken,
  getAuthToken,
  ACCOUNT_SECURITY_PATH,
  redirectToForbidden,
  redirectToLogin,
} from '@/utils/guard';

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure extends ApiErrorBody {
  success: boolean;
  data: any;
  code?: string;
  message?: string;
  errorMessage?: string;
  traceId?: string;
  displayMessage?: string;
  showType?: ErrorShowType;
}

const renderErrorMessage = (messageText: string) => {
  if (!messageText.includes('\n')) {
    return messageText;
  }
  return React.createElement('span', { style: { whiteSpace: 'pre-line' } }, messageText);
};

const isAbsoluteUrl = (url: string) => /^(?:[a-z][a-z\d+\-.]*:)?\/\//i.test(url);

const normalizeApiBaseUrl = () => {
  if (typeof API_BASE_URL !== 'string') {
    return '';
  }

  return API_BASE_URL.trim().replace(/\/+$/, '');
};

const resolveRequestUrl = (url?: string) => {
  if (!url || isAbsoluteUrl(url)) {
    return url;
  }

  const apiBaseUrl = normalizeApiBaseUrl();
  if (!apiBaseUrl || apiBaseUrl === '/api') {
    return url;
  }

  if (url === '/api') {
    return apiBaseUrl;
  }

  if (url.startsWith('/api/')) {
    return `${apiBaseUrl}${url.slice('/api'.length)}`;
  }

  return url;
};

const isLoginRequestUrl = (url?: string) => !!url && /\/api\/auth\/login(?:[?#]|$)/.test(url);

const handleAuthFailure = (messageText?: string) => {
  clearAuthToken();
  redirectToLogin(window.location.pathname + window.location.search);
  message.error(renderErrorMessage(messageText || '未登录或登录已失效'));
};

const handleForbiddenFailure = (messageText?: string) => {
  message.error(renderErrorMessage(messageText || '当前账号没有访问权限'));
  redirectToForbidden();
};

const handlePasswordChangeRequired = (messageText?: string) => {
  message.warning(renderErrorMessage(messageText || '请先修改初始密码'));
  if (window.location.pathname !== ACCOUNT_SECURITY_PATH) {
    window.location.href = ACCOUNT_SECURITY_PATH;
  }
};

export const errorConfig: RequestConfig = {
  errorConfig: {
    errorThrower: (res) => {
      const { success, data, code, errorMessage, traceId, showType } = res as unknown as ResponseStructure;
      if (!success) {
        const finalMessage = getApiErrorRawMessage(res as ApiErrorBody);
        const displayMessage = formatApiErrorMessage(res as ApiErrorBody);
        const error: any = new Error(displayMessage);
        error.name = 'BizError';
        error.info = {
          code,
          data,
          message: finalMessage,
          errorMessage,
          traceId,
          showType,
          displayMessage,
        };
        throw error;
      }
    },
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) {
        throw error;
      }

      if (error.name === 'BizError') {
        const errorInfo = error.info as ResponseStructure | undefined;
        const finalMessage = formatApiErrorMessage(errorInfo);
        const code = errorInfo?.code;
        markRequestErrorHandled(error);

        if (code === '401') {
          handleAuthFailure(finalMessage);
          return;
        }

        if (code === '403') {
          handleForbiddenFailure(finalMessage);
          return;
        }

        if (code === 'ACCOUNT_PASSWORD_CHANGE_REQUIRED') {
          handlePasswordChangeRequired(finalMessage);
          return;
        }

        switch (errorInfo?.showType) {
          case ErrorShowType.SILENT:
            return;
          case ErrorShowType.WARN_MESSAGE:
            message.warning(renderErrorMessage(finalMessage));
            return;
          case ErrorShowType.NOTIFICATION:
            notification.open({
              message: code,
              description: renderErrorMessage(finalMessage),
            });
            return;
          case ErrorShowType.REDIRECT:
          case ErrorShowType.ERROR_MESSAGE:
          default:
            message.error(renderErrorMessage(finalMessage));
            return;
        }
      }

      const responseData = error.response?.data;
      const responseMessage = isApiErrorBody(responseData)
        ? formatApiErrorMessage(responseData)
        : undefined;

      if (error.response?.status === 401) {
        markRequestErrorHandled(error);
        handleAuthFailure(responseMessage || error.response?.data?.message);
        return;
      }

      if (error.response?.status === 403) {
        markRequestErrorHandled(error);
        handleForbiddenFailure(responseMessage || error.response?.data?.message);
        return;
      }

      if (error.response) {
        markRequestErrorHandled(error);
        message.error(renderErrorMessage(responseMessage || `Response status:${error.response.status}`));
        return;
      }

      if (error.request) {
        message.error('None response! Please retry.');
        return;
      }

      message.error(renderErrorMessage(error.message || 'Request error, please retry.'));
    },
  },
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = getAuthToken();
      const headers = { ...(config.headers || {}) } as Record<string, string>;
      const hasExplicitAuthorization = Object.keys(headers).some(
        (key) => key.toLowerCase() === 'authorization',
      );
      const resolvedUrl = resolveRequestUrl(config.url);

      if (
        token &&
        !hasExplicitAuthorization &&
        !isLoginRequestUrl(config.url) &&
        !isLoginRequestUrl(resolvedUrl)
      ) {
        headers.Authorization = `Bearer ${token}`;
      }

      const nextConfig = { ...config, headers };

      if (resolvedUrl !== undefined) {
        nextConfig.url = resolvedUrl;
      }

      return nextConfig;
    },
  ],
  responseInterceptors: [
    (response) => {
      const { data } = response as unknown as { data?: ResponseStructure };

      if (response.status === 401 || data?.code === '401') {
        handleAuthFailure(formatApiErrorMessage(data, '未登录或登录已失效'));
        return response;
      }

      if (data?.code === 'ACCOUNT_PASSWORD_CHANGE_REQUIRED') {
        handlePasswordChangeRequired(formatApiErrorMessage(data, '请先修改初始密码'));
        return response;
      }

      if (response.status === 403 || data?.code === '403') {
        handleForbiddenFailure(formatApiErrorMessage(data, '当前账号没有访问权限'));
        return response;
      }

      return response;
    },
  ],
};
